import { useSearchParams } from 'next/navigation';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { PAYLOAD_QUERY } from '@/components/customercenter-ui/components/customer-detail/constants';
import CreditCardsIcon from '@/components/customercenter-ui/icons/creditCards';
import { Label } from '@/components/customercenter-ui/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/customercenter-ui/ui/radio-group';
import { getDivisionSvgs } from '@/components/customercenter-ui/utils/crediCardUtils';
import { PaymentMethods, useCheckout } from '@/context/checkoutProvider';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';

export function CardRadioButton({ data, isDisabled }: any) {
  const { selectedPaymentMethod, setSelectedPaymentMethods, isNoChargeOrder, isMailOrder } = useCheckout();
  const projectSettings: ProjectSettings = useProjSettings();
  const params = useSearchParams();
  const allowedPaymentMethods = JSON.parse(projectSettings?.allowedPaymentMethods ?? '');
  const { showCashOption = false, showCheckOption = false } = data ?? {};
  const divisionCode = params.get(PAYLOAD_QUERY.DIVISION_CODE) ?? 'ZB';
  const allowedMethodsForDivision: any = allowedPaymentMethods?.[divisionCode];

  const handlePaymentMethodChange = (event: PaymentMethods) => {
    setSelectedPaymentMethods(event);
  };

  const renderComponent = () => {
    switch (divisionCode) {
      case 'ZD':
        return 'Mason Easy-Pay®';
      case 'ZB':
        return 'Stoneberry Credit';
      case 'ZY':
        return 'Masseys Credit';
      default:
        return 'Stoneberry Credit';
    }
  };

  const renderPaymentMethods = () => {
    if (isNoChargeOrder) {
      return <></>;
    }
    return (
      <>
        {' '}
        {allowedMethodsForDivision.includes(PaymentMethods.CARD) && (
          <section className="flex items-center space-x-2.5 rounded-md border border-foggyGray pl-4 shadow-md">
            <RadioGroupItem value={PaymentMethods.CARD} id="card" />
            <Label htmlFor="card" className="flex w-full cursor-pointer items-center justify-between pl-2">
              <p className="py-4 text-sm font-normal leading-6 md:text-lg">Credit/Debit Card</p>
              <CreditCardsIcon className="gbomit h-9 w-[149px] pr-4" />
            </Label>
          </section>
        )}
        {allowedMethodsForDivision.includes(PaymentMethods.CREDIT) && (
          <section
            className={`flex items-center space-x-2.5 rounded-md border border-foggyGray pl-4 shadow-md ${
              isDisabled ? 'disabled-section' : ''
            }`}
          >
            <RadioGroupItem value={PaymentMethods.CREDIT} id="credit" disabled={isDisabled} />
            <Label htmlFor="credit" className="flex w-full cursor-pointer items-center justify-between pl-2">
              <p className="py-4 text-sm font-normal leading-6 md:text-lg">{renderComponent()}</p>
              <div className="gbomit mr-4 flex w-24 items-start">
                {getDivisionSvgs(divisionCode, 'gbomit h-9 w-[149px] pr-4')}
              </div>
            </Label>
          </section>
        )}
        {allowedMethodsForDivision.includes(PaymentMethods.CASH) && showCashOption && isMailOrder && (
          <section className="flex items-center space-x-2.5 rounded-md border border-foggyGray pl-4 shadow-md">
            <RadioGroupItem value={PaymentMethods.CASH} id="cash" />
            <Label htmlFor="cash" className="flex w-full cursor-pointer items-center justify-between pl-2">
              <p className="py-4 text-lg font-normal leading-6">Cash</p>
            </Label>
          </section>
        )}
        {allowedMethodsForDivision.includes(PaymentMethods.CHECK) && showCheckOption && isMailOrder && (
          <section className="flex items-center space-x-2.5 rounded-md border border-foggyGray pl-4 shadow-md">
            <RadioGroupItem value={PaymentMethods.CHECK} id="check" />
            <Label htmlFor="check" className="flex w-full cursor-pointer items-center justify-between pl-2">
              <p className="py-4 text-lg font-normal leading-6">Check(s)</p>
            </Label>
          </section>
        )}
      </>
    );
  };

  return (
    <>
      {selectedPaymentMethod && (
        <RadioGroup
          value={selectedPaymentMethod}
          onValueChange={(value: PaymentMethods) => handlePaymentMethodChange(value)}
          className="grid-col grid grid-cols-2 gap-3"
        >
          {renderPaymentMethods()}
        </RadioGroup>
      )}
    </>
  );
}
